/**
 * Audit Log API
 * Provides access to audit log entries for tracking data changes
 */

import { supabase } from '../lib/supabase';
import { createAPIMethod, createPaginatedAPIMethod } from './base';

/**
 * Get audit trail for a specific record
 * @param {string} tableName - Name of the table
 * @param {string} recordId - ID of the record
 * @param {number} limit - Maximum number of entries to return
 * @returns {Promise<Object>} Audit trail entries
 */
const getAuditTrail = createAPIMethod(
  async (tableName, recordId, limit = 100) => {
    const { data, error } = await supabase
      .rpc('get_audit_trail', {
        p_table_name: tableName,
        p_record_id: recordId,
        p_limit: limit
      });

    if (error) throw error;
    return data;
  },
  { operation: 'Get audit trail for record' }
);

/**
 * Get audit logs with filters
 * @param {Object} filters - Filter criteria
 * @param {string} filters.tableName - Filter by table name
 * @param {string} filters.recordId - Filter by record ID
 * @param {string} filters.action - Filter by action (INSERT, UPDATE, DELETE)
 * @param {string} filters.changedBy - Filter by user ID
 * @param {Date} filters.startDate - Filter by start date
 * @param {Date} filters.endDate - Filter by end date
 * @param {number} filters.limit - Maximum number of entries
 * @param {number} filters.offset - Offset for pagination
 * @returns {Promise<Object>} Filtered audit log entries
 */
const getAuditLogs = createAPIMethod(
  async (filters = {}) => {
    let query = supabase
      .from('audit_log')
      .select(`
        id,
        table_name,
        record_id,
        action,
        old_values,
        new_values,
        changed_by,
        changed_at,
        users:changed_by (
          id,
          email,
          first_name,
          last_name
        )
      `)
      .order('changed_at', { ascending: false });

    // Apply filters
    if (filters.tableName) {
      query = query.eq('table_name', filters.tableName);
    }

    if (filters.recordId) {
      query = query.eq('record_id', filters.recordId);
    }

    if (filters.action) {
      query = query.eq('action', filters.action);
    }

    if (filters.changedBy) {
      query = query.eq('changed_by', filters.changedBy);
    }

    if (filters.startDate) {
      query = query.gte('changed_at', filters.startDate.toISOString());
    }

    if (filters.endDate) {
      query = query.lte('changed_at', filters.endDate.toISOString());
    }

    // Apply pagination
    const limit = filters.limit || 50;
    const offset = filters.offset || 0;
    query = query.range(offset, offset + limit - 1);

    const { data, error, count } = await query;

    if (error) throw error;

    return {
      data,
      count,
      limit,
      offset
    };
  },
  { operation: 'Get audit logs with filters' }
);

/**
 * Get paginated audit logs
 * @param {Object} options - Pagination and filter options
 * @returns {Promise<Object>} Paginated audit log entries
 */
const getPaginated = createPaginatedAPIMethod(
  async ({ page = 1, pageSize = 50, filters = {} }) => {
    const offset = (page - 1) * pageSize;

    const result = await getAuditLogs.fn({
      ...filters,
      limit: pageSize,
      offset
    });

    return {
      data: result.data,
      pagination: {
        page,
        pageSize,
        total: result.count,
        totalPages: Math.ceil((result.count || 0) / pageSize)
      }
    };
  },
  { operation: 'Get paginated audit logs' }
);

/**
 * Get field changes between old and new values
 * @param {Object} oldValues - Old JSONB values
 * @param {Object} newValues - New JSONB values
 * @returns {Promise<Object>} Field-level changes
 */
const getFieldChanges = createAPIMethod(
  async (oldValues, newValues) => {
    const { data, error } = await supabase
      .rpc('get_field_changes', {
        p_old_values: oldValues || null,
        p_new_values: newValues || null
      });

    if (error) throw error;
    return data;
  },
  { operation: 'Get field changes' }
);

/**
 * Get audit summary statistics
 * @param {Object} filters - Filter criteria
 * @returns {Promise<Object>} Audit statistics
 */
const getAuditSummary = createAPIMethod(
  async (filters = {}) => {
    let query = supabase
      .from('audit_log')
      .select('action, table_name, changed_at', { count: 'exact' });

    // Apply filters
    if (filters.tableName) {
      query = query.eq('table_name', filters.tableName);
    }

    if (filters.startDate) {
      query = query.gte('changed_at', filters.startDate.toISOString());
    }

    if (filters.endDate) {
      query = query.lte('changed_at', filters.endDate.toISOString());
    }

    const { data, error, count } = await query;

    if (error) throw error;

    // Calculate statistics
    const stats = {
      total: count || 0,
      byAction: {},
      byTable: {},
      recent: data?.slice(0, 10) || []
    };

    // Group by action
    data?.forEach(entry => {
      stats.byAction[entry.action] = (stats.byAction[entry.action] || 0) + 1;
      stats.byTable[entry.table_name] = (stats.byTable[entry.table_name] || 0) + 1;
    });

    return stats;
  },
  { operation: 'Get audit summary' }
);

/**
 * Get reservation history (convenience method)
 * @param {string} reservationId - Reservation ID
 * @returns {Promise<Object>} Reservation audit trail
 */
const getReservationHistory = createAPIMethod(
  async (reservationId) => {
    return await getAuditTrail.fn('reservations', reservationId);
  },
  { operation: 'Get reservation history' }
);

/**
 * Get user activity log
 * @param {string} userId - User ID
 * @param {number} limit - Maximum number of entries
 * @returns {Promise<Object>} User's audit log entries
 */
const getUserActivity = createAPIMethod(
  async (userId, limit = 100) => {
    const { data, error } = await supabase
      .from('audit_log')
      .select(`
        id,
        table_name,
        record_id,
        action,
        changed_at
      `)
      .eq('changed_by', userId)
      .order('changed_at', { ascending: false })
      .limit(limit);

    if (error) throw error;
    return data;
  },
  { operation: 'Get user activity' }
);

/**
 * Compare two audit log entries
 * @param {string} entryId1 - First entry ID
 * @param {string} entryId2 - Second entry ID
 * @returns {Promise<Object>} Comparison of changes
 */
const compareEntries = createAPIMethod(
  async (entryId1, entryId2) => {
    const { data, error } = await supabase
      .from('audit_log')
      .select('*')
      .in('id', [entryId1, entryId2]);

    if (error) throw error;

    if (data.length !== 2) {
      throw new Error('Could not find both entries');
    }

    const [entry1, entry2] = data;

    // Get field changes for each entry
    const changes1 = await getFieldChanges.fn(entry1.old_values, entry1.new_values);
    const changes2 = await getFieldChanges.fn(entry2.old_values, entry2.new_values);

    return {
      entry1: {
        ...entry1,
        changes: changes1
      },
      entry2: {
        ...entry2,
        changes: changes2
      }
    };
  },
  { operation: 'Compare audit entries' }
);

// Export all methods
export const auditLogAPI = {
  getAuditTrail,
  getAuditLogs,
  getPaginated,
  getFieldChanges,
  getAuditSummary,
  getReservationHistory,
  getUserActivity,
  compareEntries
};

export default auditLogAPI;
